import React, { useState } from "react";

import t from "../../lib/translate";

import { Button } from "../../components/Button";
import { Headline } from "../../components/Headline";
import { SectionContainer } from "../../components/SectionContainer";
import { StructuredTextRenderer } from "../../components/StructuredTextRenderer";
import { Tagline } from "../../components/Tagline/Tagline";
import * as styles from "./cameraTechnicalDetail.module.less";

const showAllThreshold = 13;

function CameraTechnicalDetail({
    data: {
        anker,
        tagline,
        headline,
        semanticOfHeadline,
        subheadline,
        background,
        camera: {
            spectralRange,
            pitch,
            detectorType,
            detectorFormat,
            imageFormatOptomechanicalMicroscan,
            imageRecordingPrinciple,
            readingMode,
            apertur,
            detectorCooling,
            temperatureMeasuringRange,
            measuringAccuracy,
            temperatureResolution,
            irFrameRateFfHfQfPf,
            windowMode,
            windowModeOptional,
            storageMedia,
            imageStorage,
            realtimeStorage,
            realtimeStorageOptional,
            lenseMount,
            focus,
            eversharpFunction,
            eversharpFunctionOptional,
            focusTime,
            lenseFocalLength,
            zoomAdjustmentTime,
            fieldOfViewIrPixels,
            visualField,
            closeUpFocus,
            closeUpFocusOptional,
            maximumDetectionRange,
            maximumIdentificationRange,
            zoom,
            digitalColourVideoCamera,
            digitalColourVideoCameraOptional,
            detectionRangeOfOnePerson,
            detectionRangeOfAVehicle,
            dynamicRange,
            integrationTime,
            rotatingApertureWheel,
            rotatingApertureWheelOptional,
            rotatingFilterWheel,
            rotatingFilterWheelOptional,
            multiIntegrationTime,
            multiIntegrationTimeOptional,
            pictureSynchronisation,
            pictureSynchronisationOptional,
            interfaces,
            interfacesOptional,
            trigger,
            triggerOptional,
            analogSignalsIrigB,
            analogSignalsIrigBOptional,
            tripodMount,
            powerSupply,
            integratedMicrophoneAndLoudspeaker,
            integratedMicrophoneAndLoudspeakerOptional,
            laserRangefinder,
            laserRangefinderOptional,
            integratedGpsSensor,
            integratedGpsSensorOptional,
            display,
            colorFinder,
            colorFinderOptional,
            handleWithWristStrap,
            handleWithOperatingFunction,
            handleWithOperatingFunctionOptional,
            singleHandOperation,
            shoulderStrap,
            storageAndOperatingTemperature,
            protectionLevel,
            shockVibrationResistanceInOperation,
            dimensionsWeight,
            automaticFunctions,
            automaticFunctionsOptional,
            measurementFunctions,
            measurementFunctionsOptional,
            moreFunctions,
            moreFunctionsOptional,
            analysisAndEvaluationSoftware,
            analysisAndEvaluationSoftwareOptional
        }
    },
    hasLightBackground
}) {
    const properties = [
        {
            key: t("camera.property.spectralrange"),
            value: spectralRange,
            optional: false
        },
        { key: t("camera.property.pitch"), value: pitch, optional: false },
        {
            key: t("camera.property.detectortype"),
            value: detectorType,
            optional: false
        },
        {
            key: t("camera.property.detectorformat"),
            value: detectorFormat,
            optional: false
        },
        {
            key: t("camera.property.imageformat"),
            value: imageFormatOptomechanicalMicroscan,
            optional: false
        },
        {
            key: t("camera.property.imagerecordingprinciple"),
            value: imageRecordingPrinciple,
            optional: false
        },
        {
            key: t("camera.property.readingmode"),
            value: readingMode,
            optional: false
        },
        { key: t("camera.property.aperture"), value: apertur, optional: false },
        {
            key: t("camera.property.detectorcooling"),
            value: detectorCooling,
            optional: false
        },
        {
            key: t("camera.property.temperaturemeasuringrange"),
            value: temperatureMeasuringRange,
            optional: false
        },
        {
            key: t("camera.property.measuringaccuracy"),
            value: measuringAccuracy,
            optional: false
        },
        {
            key: t("camera.property.temperatureresolution"),
            value: temperatureResolution,
            optional: false
        },
        {
            key: t("camera.property.irframe"),
            value: irFrameRateFfHfQfPf,
            optional: false
        },
        {
            key: t("camera.property.windowmode"),
            value: windowMode,
            optional: windowModeOptional
        },
        {
            key: t("camera.property.storagemedia"),
            value: storageMedia,
            optional: false
        },
        {
            key: t("camera.property.imagestorage"),
            value: imageStorage,
            optional: false
        },
        {
            key: t("camera.property.realtimestorage"),
            value: realtimeStorage,
            optional: realtimeStorageOptional
        },
        {
            key: t("camera.property.lensmount"),
            value: lenseMount,
            optional: false
        },
        { key: t("camera.property.focus"), value: focus, optional: false },
        {
            key: t("camera.property.eversharpfunction"),
            value: eversharpFunction,
            optional: eversharpFunctionOptional
        },
        {
            key: t("camera.property.focustime"),
            value: focusTime,
            optional: false
        },
        {
            key: t("camera.property.lensfocallength"),
            value: lenseFocalLength,
            optional: false
        },
        {
            key: t("camera.property.zoomadjustmenttime"),
            value: zoomAdjustmentTime,
            optional: false
        },
        {
            key: t("camera.property.fieldofview"),
            value: fieldOfViewIrPixels,
            optional: false
        },
        {
            key: t("camera.property.visualfield"),
            value: visualField,
            optional: false
        },
        {
            key: t("camera.property.closeupfocus"),
            value: closeUpFocus,
            optional: closeUpFocusOptional
        },
        {
            key: t("camera.property.maxdetectionrange"),
            value: maximumDetectionRange,
            optional: false
        },
        {
            key: t("camera.property.maximumidentificationrange"),
            value: maximumIdentificationRange,
            optional: false
        },
        { key: t("camera.property.zoom"), value: zoom, optional: false },
        {
            key: t("camera.property.digitalcolourvideocamera"),
            value: digitalColourVideoCamera,
            optional: digitalColourVideoCameraOptional
        },
        {
            key: t("camera.property.detectionrangeperson"),
            value: detectionRangeOfOnePerson,
            optional: false
        },
        {
            key: t("camera.property.detectionrangevehicle"),
            value: detectionRangeOfAVehicle,
            optional: false
        },
        {
            key: t("camera.property.dynamicrange"),
            value: dynamicRange,
            optional: false
        },
        {
            key: t("camera.property.integrationtime"),
            value: integrationTime,
            optional: false
        },
        {
            key: t("camera.property.rotatingaperturewheel"),
            value: rotatingApertureWheel,
            optional: rotatingApertureWheelOptional
        },
        {
            key: t("camera.property.rotatingfilterwheel"),
            value: rotatingFilterWheel,
            optional: rotatingFilterWheelOptional
        },
        {
            key: t("camera.property.multiintegrationtime"),
            value: multiIntegrationTime,
            optional: multiIntegrationTimeOptional
        },
        {
            key: t("camera.property.picturesynchronisation"),
            value: pictureSynchronisation,
            optional: pictureSynchronisationOptional
        },
        {
            key: t("camera.property.interfaces"),
            value: interfaces,
            optional: interfacesOptional
        },
        {
            key: t("camera.property.trigger"),
            value: trigger,
            optional: triggerOptional
        },
        {
            key: t("camera.property.analogsignals"),
            value: analogSignalsIrigB,
            optional: analogSignalsIrigBOptional
        },
        {
            key: t("camera.property.tripodmount"),
            value: tripodMount,
            optional: false
        },
        {
            key: t("camera.property.powersupply"),
            value: powerSupply,
            optional: false
        },
        {
            key: t("camera.property.integratedmicrophoneandloudspeaker"),
            value: integratedMicrophoneAndLoudspeaker,
            optional: integratedMicrophoneAndLoudspeakerOptional
        },
        {
            key: t("camera.property.laserrangefinder"),
            value: laserRangefinder,
            optional: laserRangefinderOptional
        },
        {
            key: t("camera.property.integratedgpssensor"),
            value: integratedGpsSensor,
            optional: integratedGpsSensorOptional
        },
        { key: t("camera.property.display"), value: display, optional: false },
        {
            key: t("camera.property.colourfinder"),
            value: colorFinder,
            optional: colorFinderOptional
        },
        {
            key: t("camera.property.handlewithwriststrap"),
            value: handleWithWristStrap,
            optional: false
        },
        {
            key: t("camera.property.handlewithoperatingfunction"),
            value: handleWithOperatingFunction,
            optional: handleWithOperatingFunctionOptional
        },
        {
            key: t("camera.property.singlehandoperation"),
            value: singleHandOperation,
            optional: false
        },
        {
            key: t("camera.property.shoulderstrap"),
            value: shoulderStrap,
            optional: false
        },
        {
            key: t("camera.property.storageandoperatingtemperature"),
            value: storageAndOperatingTemperature,
            optional: false
        },
        {
            key: t("camera.property.protectionlevel"),
            value: protectionLevel,
            optional: false
        },
        {
            key: t("camera.property.shockvibrationresistance"),
            value: shockVibrationResistanceInOperation,
            optional: false
        },
        {
            key: t("camera.property.dimensionsweight"),
            value: dimensionsWeight,
            optional: false
        },
        {
            key: t("camera.property.automaticfunctions"),
            value: automaticFunctions,
            optional: automaticFunctionsOptional
        },
        {
            key: t("camera.property.morefunctions"),
            value: moreFunctions,
            optional: moreFunctionsOptional
        },
        {
            key: t("camera.property.analysisandevaluationsoftware"),
            value: analysisAndEvaluationSoftware,
            optional: analysisAndEvaluationSoftwareOptional
        },
        {
            key: t("camera.property.measurementfunctions"),
            value: measurementFunctions,
            optional: measurementFunctionsOptional
        }
    ];

    const [showAll, setShowAll] = useState(false);

    return (
        <SectionContainer
            sectionId={anker}
            background={background}
            sectionClass={styles.technicalDetails}
            containerClass={styles.detailContainer}
        >
            <Tagline hasLightBackground={hasLightBackground}>{tagline}</Tagline>
            <Headline
                semanticOfHeadline={semanticOfHeadline}
                hasLightBackground={hasLightBackground}
            >
                {headline}
            </Headline>
            <StructuredTextRenderer data={subheadline} />
            <div className={styles.tableWrapper}>
                <table>
                    <tbody>
                        {properties.map((cameraDetail, idx) => (
                            <React.Fragment key={cameraDetail.key}>
                                {cameraDetail.value &&
                                    (!showAll
                                        ? idx <= showAllThreshold
                                        : true) && (
                                        <tr>
                                            <td>
                                                <span>{cameraDetail.key}</span>
                                            </td>
                                            <td>
                                                <span>
                                                    {cameraDetail.value}
                                                    {cameraDetail.optional &&
                                                        "*"}
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <span
                className={styles.tableInfo}
            >{t`camera.propierties-table.info`}</span>

            {!showAll && (
                <>
                    <Button
                        type="primary"
                        icon="chevronDown"
                        iconPosition="right"
                        onClick={() => setShowAll(true)}
                        className={styles.expandButton}
                    >
                        {t`camera.propierties-table.showAll`}
                    </Button>
                </>
            )}
        </SectionContainer>
    );
}

export { CameraTechnicalDetail };
